import React from 'react';
import { TrendingUp, Minus, AlertTriangle, ShieldCheck } from 'lucide-react';
import { CONDITION_STAGES, conditionBand, BAND_TONE } from '../../lib/leiConditions';

export const TRAJECTORY_META = {
  improving: { label: 'Improving', tone: 'text-emerald-700 bg-emerald-100 border-emerald-200', Icon: TrendingUp },
  stable: { label: 'Stable', tone: 'text-slate-600 bg-slate-100 border-slate-200', Icon: Minus },
  at_risk: { label: 'At Risk', tone: 'text-rose-700 bg-rose-100 border-rose-200', Icon: AlertTriangle },
};

/** Small inline badges for a program's Trajectory + Navigation Confidence. */
export function ProgramNavBadges({ program }) {
  const tj = TRAJECTORY_META[program.trajectory] || TRAJECTORY_META.stable;
  const conf = program.navigation_confidence;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${tj.tone}`} data-testid="program-trajectory">
        <tj.Icon className="h-3 w-3" /> {tj.label}
      </span>
      {conf != null && (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[11px] font-bold text-cyan-700"
          title="Navigation Confidence — how confident we are the value pathway is behaving as expected. A transparent composite of data sufficiency (verified outcomes), trajectory (LEI improvement), and conversion (net benefit)."
          data-testid="program-nav-confidence"
        >
          <ShieldCheck className="h-3 w-3" /> {conf}% confidence
        </span>
      )}
    </div>
  );
}

/**
 * Navigation Conditions panel — replaces the single LEI progress bar with
 * the 5 Missing Middle condition stages (real per-program LEI data, same
 * pillar→stage grouping as the rest of the app) plus a Strong / Primary
 * Constraint read-out. Answers "where is value most likely leaking?".
 */
export function ProgramNavigationPanel({ program }) {
  const conditions = program.navigation_conditions || {};
  const hasAny = CONDITION_STAGES.some((s) => conditions[s.key] != null);
  if (!hasAny) return null;
  const strong = program.strongest_stage;
  const weak = program.weakest_stage;

  return (
    <div className="mt-4 pt-4 border-t" data-testid={`program-nav-conditions-${program.program_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Navigation Conditions</span>
        {program.conversion_efficiency != null && (
          <span className="text-[11px] text-slate-500" title="Capability → Value conversion (impact ÷ investment)">
            Capability → Value <span className="font-bold text-slate-700">{Math.round(program.conversion_efficiency)}%</span>
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {CONDITION_STAGES.map((s) => {
          const score = conditions[s.key];
          const band = conditionBand(score);
          return (
            <div key={s.key} className="flex items-center gap-2" data-testid={`prog-stage-${s.key}`}>
              <span className="w-24 shrink-0 text-[11px] font-medium text-slate-600">{s.label}</span>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, score ?? 0))}%`, backgroundColor: s.color }} />
              </div>
              <span className="w-7 shrink-0 text-right text-[11px] font-bold tabular-nums text-slate-700">
                {score != null ? Math.round(score) : '—'}
              </span>
              <span className={`w-20 shrink-0 text-center rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${BAND_TONE[band.tone]}`}>
                {band.label}
              </span>
            </div>
          );
        })}
      </div>
      {(strong || weak) && (
        <div className="mt-2.5 flex items-center gap-4 text-[11px] flex-wrap">
          {strong && <span className="text-slate-600">Strong: <span className="font-bold text-emerald-700">{strong.label}</span></span>}
          {weak && <span className="text-slate-600">Primary Constraint: <span className="font-bold text-rose-700">{weak.label}</span></span>}
        </div>
      )}
    </div>
  );
}
